/**
 * The full-size map's step C (DIRECTION §0.11, "the route graph ... the spine: convoys, bots' long walks, the ship's choice
 * and the table's routes all read it"; the walks measured and left straight — see BUILDLIST 69.8 step C): the planet's coarse route graph — the terrain mesh's own vertices (~10k on a
 * megaplanet, ~17 people apart at the human scale) joined by the mesh's edges, each edge's grade exact (the coarse ground is
 * linear between vertices). Built from the terrain alone, as generated (`initialHeightmap ?? heightmap`): no obstacles (the
 * local steer and D3's person-scale nav, core/surfaceNav.ts, keep those), so it is a pure function of the planet, built once
 * and never stale. Two ways over it: on foot and on wheels, each its own pace by grade and its own steepest edge refused.
 *
 * A trip reads a **field**: a Dijkstra search out from the destination's vertex, every vertex's cost to go and its next hop
 * (memoised per destination vertex and way; a pure function, so the memo never changes an answer). From the field any
 * number of walkers read their next waypoint and their trip's cost in O(1). Costs are flat-equivalent world units: a
 * second at a pace is the cost ÷ that pace. Craters after generation do not reroute (their bowls are person-scale).
 */
import type { GameConfig, GameState, PlanetState, Vec3 } from '../types';
import { ROUTE } from '../constants/action';
import { nearestSurfaceVertex } from './surface';
import { dacos } from './dmath';

export type RouteWay = 'foot' | 'wheels';

/** The graph: unique vertices (their directions and ground radii) and the edges as CSR adjacency; `nodeOf` maps a mesh position to its vertex. */
export interface PlanetRouteGraph {
  count: number;
  dirs: Float64Array;
  radii: Float64Array;
  nodeOf: Int32Array;
  start: Uint32Array;
  nbr: Uint32Array;
}

/** A trip's field toward one destination vertex: each vertex's cost to go (Infinity: no way) and its next hop (−1 at the destination or with no way). */
export interface RouteField { node: number; cost: Float64Array; next: Int32Array }

/**
 * Whether long trips on `planet` follow its route graph: with the switch (GameConfig.planetRoutes), or on a world that took
 * 74.13's landform pass (the Landforms row: `baseGroundKey` is set only there) — a range is a barrier the routes cross at its
 * passes, so the row brings the routes with it (step 5, ARCHITECTURE §5.47). Read by the drives (ai/rideBrain.ts), the ship
 * ride's choice (core/shipRide.ts) and, on a landform world, the people's long walks (ai/actionBot.ts).
 */
export function routesOn(config: Pick<GameConfig, 'planetRoutes'>, planet: Pick<PlanetState, 'baseGroundKey'>): boolean {
  return !!config.planetRoutes || planet.baseGroundKey !== undefined;
}

const graphs = new WeakMap<Float32Array, PlanetRouteGraph>();
const fieldMemo = new WeakMap<PlanetRouteGraph, Map<string, RouteField>>();

/** The planet's route graph (built on first ask, then cached by its mesh and generated heights). */
export function planetRouteGraph(planet: PlanetState): PlanetRouteGraph {
  const heights = planet.initialHeightmap ?? planet.heightmap;
  const hit = graphs.get(heights);
  if (hit) return hit;
  const bp = planet.basePositions, positions = bp.length / 3;
  // unique vertices: a shared vertex repeats its exact position in every triangle that holds it
  const nodeOf = new Int32Array(positions);
  const seen = new Map<string, number>();
  const firstPos: number[] = [];
  for (let i = 0; i < positions; i++) {
    const key = `${bp[3 * i]},${bp[3 * i + 1]},${bp[3 * i + 2]}`;
    let id = seen.get(key);
    if (id === undefined) { id = firstPos.length; seen.set(key, id); firstPos.push(i); }
    nodeOf[i] = id;
  }
  const count = firstPos.length;
  const dirs = new Float64Array(3 * count), radii = new Float64Array(count);
  for (let v = 0; v < count; v++) {
    const i = firstPos[v], x = bp[3 * i], y = bp[3 * i + 1], z = bp[3 * i + 2];
    const len = Math.sqrt(x * x + y * y + z * z);
    dirs[3 * v] = x / len; dirs[3 * v + 1] = y / len; dirs[3 * v + 2] = z / len;
    radii[v] = len + heights[i];
  }
  // edges: each triangle's three sides, once a pair, both ways
  const pairs = new Set<number>();
  const deg = new Uint32Array(count);
  const tris = Math.floor(positions / 3);
  const link = (a: number, b: number) => { if (a === b) return; const k = a < b ? a * count + b : b * count + a; if (pairs.has(k)) return; pairs.add(k); deg[a]++; deg[b]++; };
  for (let t = 0; t < tris; t++) { const a = nodeOf[3 * t], b = nodeOf[3 * t + 1], c = nodeOf[3 * t + 2]; link(a, b); link(b, c); link(c, a); }
  const start = new Uint32Array(count + 1);
  for (let v = 0; v < count; v++) start[v + 1] = start[v] + deg[v];
  const fill = start.slice(0, count), nbr = new Uint32Array(start[count]);
  for (const k of [...pairs].sort((p, q) => p - q)) { const a = Math.floor(k / count), b = k % count; nbr[fill[a]++] = b; nbr[fill[b]++] = a; }
  const g: PlanetRouteGraph = { count, dirs, radii, nodeOf, start, nbr };
  graphs.set(heights, g);
  return g;
}

const dirOf = (g: PlanetRouteGraph, v: number): Vec3 => ({ x: g.dirs[3 * v], y: g.dirs[3 * v + 1], z: g.dirs[3 * v + 2] });
const arcBetween = (a: Vec3, b: Vec3): number => {
  const la = Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z), lb = Math.sqrt(b.x * b.x + b.y * b.y + b.z * b.z);
  return dacos(Math.max(-1, Math.min(1, (a.x * b.x + a.y * b.y + a.z * b.z) / (la * lb))));
};

/**
 * The cost of the edge from `a` to `b` (flat-equivalent world units), Infinity where that way refuses it. 74.5 step 2: with a
 * road network that carries the world (`roads.world`), wheels never enter a vertex it refuses (a strong chain's but at its
 * fords), cross a playa at ROUTE.playaWheels, and — while the roads are laid — run along a chain's bank at ROUTE.bankLay
 * and onto a lake's floor at ROUTE.lakeLay; a trail's edge counts ROUTE.trailWheels / trailFoot.
 */
export function edgeCost(g: PlanetRouteGraph, a: number, b: number, way: RouteWay, planetRadius: number, roads?: Roads | null): number {
  const L0 = arcBetween(dirOf(g, a), dirOf(g, b)) * planetRadius;
  if (L0 < 1e-12) return 0;
  const world = way === 'wheels' ? roads?.world : undefined;
  if (world?.refuse[b]) return Infinity;
  // (216 (4), GameConfig.roads: a road edge is cheap transit — its length counts ROUTE.roadWheels on wheels, ROUTE.roadFoot on foot)
  const k = a < b ? a * g.count + b : b * g.count + a;
  const L = roads && roads.edges.has(k) ? L0 * (way === 'wheels' ? ROUTE.roadWheels : ROUTE.roadFoot)
    : roads?.trails?.has(k) ? L0 * (way === 'wheels' ? ROUTE.trailWheels : ROUTE.trailFoot)
    : world && world.firm[a] && world.firm[b] ? L0 * ROUTE.playaWheels
    : world?.lake?.[b] ? L0 * ROUTE.lakeLay
    : world?.bank && world.bank[a] && world.bank[b] ? L0 * ROUTE.bankLay
    : L0;
  const grade = (g.radii[b] - g.radii[a]) / L0;
  if (way === 'foot') {
    if (grade > ROUTE.footMaxGrade) return Infinity;   // (a climb the walk refuses; down is always allowed, as stepWalk's)
    return L * (1 + ROUTE.footUphill * Math.max(0, grade) + ROUTE.footDownhill * Math.max(0, -grade - ROUTE.footEasyDown));
  }
  if (Math.abs(grade) > ROUTE.wheelsMaxGrade) return Infinity;
  return L * (1 + ROUTE.wheelsUphill * Math.max(0, grade));
}

const edgeMemo = new WeakMap<PlanetRouteGraph, Map<string, Float64Array>>();
/**
 * 74.5: every edge's cost toward its vertex, per CSR slot — slot k of vertex u (its neighbour w = nbr[k]) holds the walk from
 * w to u (`edgeCost(g, w, u, …)`, the field's own relaxation): the same numbers, worked out once a graph, way, radius and
 * road network instead of once a relaxation (a world sheet's betweenness walks 24 fields; the arc's acos was most of it).
 */
function edgeCosts(g: PlanetRouteGraph, way: RouteWay, planetRadius: number, roads?: Roads | null): Float64Array {
  let memo = edgeMemo.get(g);
  if (!memo) { memo = new Map(); edgeMemo.set(g, memo); }
  const key = `${way}:${planetRadius}:${roads?.key ?? ''}`;
  const hit = memo.get(key);
  if (hit) return hit;
  const out = new Float64Array(g.nbr.length);
  for (let u = 0; u < g.count; u++) for (let k = g.start[u]; k < g.start[u + 1]; k++) out[k] = edgeCost(g, g.nbr[k], u, way, planetRadius, roads);
  memo.set(key, out);
  while (memo.size > ROUTE.fieldMemo) memo.delete(memo.keys().next().value!);
  return out;
}

/** 74.5: `v`, or where wheels may not enter it (`refuse`), the nearest vertex they may (by hops, the lowest first): a field's destination on a strong chain's bank is reached beside it. */
function openNode(g: PlanetRouteGraph, v: number, refuse: Uint8Array | undefined): number {
  if (!refuse?.[v]) return v;
  const seen = new Set<number>([v]), queue = [v];
  for (let q = 0; q < queue.length; q++) {
    const u = queue[q];
    for (let k = g.start[u]; k < g.start[u + 1]; k++) { const w = g.nbr[k]; if (seen.has(w)) continue; if (!refuse[w]) return w; seen.add(w); queue.push(w); }
  }
  return v;
}

const mainGround = new WeakMap<PlanetRouteGraph, Uint8Array>();
/**
 * 74.13 step 7: the wheels' main ground — the vertices of the largest set wheels join (their edges refuse a grade either way,
 * so the sets are closed), as generated. On a landform world an impact massif's floor can be ringed by rims past the
 * wheels' grade (tf-1, pallor: a board base stood on a 16-vertex floor, its vehicles shut in); layOutBoard keeps the bases on this.
 */
export function wheelsMainGround(planet: PlanetState): Uint8Array {
  const g = planetRouteGraph(planet);
  const hit = mainGround.get(g);
  if (hit) return hit;
  const edges = edgeCosts(g, 'wheels', planet.radius);
  const comp = new Int32Array(g.count).fill(-1), sizes: number[] = [];
  for (let v0 = 0; v0 < g.count; v0++) {
    if (comp[v0] >= 0) continue;
    const c = sizes.length, queue = [v0];
    comp[v0] = c;
    for (let q = 0; q < queue.length; q++) {
      const u = queue[q];
      for (let k = g.start[u]; k < g.start[u + 1]; k++) { const w = g.nbr[k]; if (comp[w] < 0 && Number.isFinite(edges[k])) { comp[w] = c; queue.push(w); } }
    }
    sizes.push(queue.length);
  }
  let best = 0;
  for (let c = 1; c < sizes.length; c++) if (sizes[c] > sizes[best]) best = c;
  const out = new Uint8Array(g.count);
  for (let v = 0; v < g.count; v++) out[v] = comp[v] === best ? 1 : 0;
  mainGround.set(g, out);
  return out;
}

/** 74.13 step 7: is `dir` on the wheels' main ground (its nearest vertex's set; `wheelsMainGround`)? */
export function onWheelsMainGround(planet: PlanetState, dir: Vec3): boolean {
  return wheelsMainGround(planet)[planetRouteGraph(planet).nodeOf[nearestSurfaceVertex(planet, dir)]] === 1;
}

/** The mesh's spacing at vertex 0, as an arc (radians): the route graph's step. */
export function routeSpacingArc(planet: PlanetState): number {
  const g = planetRouteGraph(planet);
  return arcBetween(dirOf(g, 0), dirOf(g, g.nbr[g.start[0]]));
}

/** The field toward `dest` on `way` (memoised per destination vertex). */
export function routeField(planet: PlanetState, dest: Vec3, way: RouteWay, roads?: Roads | null): RouteField {
  const g = planetRouteGraph(planet);
  const node = openNode(g, g.nodeOf[nearestSurfaceVertex(planet, dest)], way === 'wheels' ? roads?.world?.refuse : undefined);
  let memo = fieldMemo.get(g);
  if (!memo) { memo = new Map(); fieldMemo.set(g, memo); }
  const key = `${way}:${node}:${roads?.key ?? ''}`;
  const hit = memo.get(key);
  if (hit) { memo.delete(key); memo.set(key, hit); return hit; }
  const cost = new Float64Array(g.count).fill(Infinity), next = new Int32Array(g.count).fill(-1);
  const done = new Uint8Array(g.count);
  // a binary heap of (cost, vertex), ties to the lower vertex
  const heapV: number[] = [], heapC: number[] = [];
  const less = (i: number, j: number) => heapC[i] < heapC[j] || (heapC[i] === heapC[j] && heapV[i] < heapV[j]);
  const swap = (i: number, j: number) => { const v = heapV[i], c = heapC[i]; heapV[i] = heapV[j]; heapC[i] = heapC[j]; heapV[j] = v; heapC[j] = c; };
  const push = (v: number, c: number) => {
    heapV.push(v); heapC.push(c);
    for (let i = heapV.length - 1; i > 0;) { const p = (i - 1) >> 1; if (!less(i, p)) break; swap(i, p); i = p; }
  };
  const pop = (): number => {
    const v = heapV[0], lastV = heapV.pop()!, lastC = heapC.pop()!;
    if (heapV.length > 0) {
      heapV[0] = lastV; heapC[0] = lastC;
      for (let i = 0; ;) { const l = 2 * i + 1, r = l + 1; let m = i; if (l < heapV.length && less(l, m)) m = l; if (r < heapV.length && less(r, m)) m = r; if (m === i) break; swap(i, m); i = m; }
    }
    return v;
  };
  const edges = edgeCosts(g, way, planet.radius, roads);
  cost[node] = 0; push(node, 0);
  while (heapV.length > 0) {
    const u = pop();
    if (done[u]) continue;
    done[u] = 1;
    for (let k = g.start[u]; k < g.start[u + 1]; k++) {
      const w = g.nbr[k];
      if (done[w]) continue;
      const c = cost[u] + edges[k];   // (the walk from w to u: toward the destination — edgeCost's, from the table)
      if (c < cost[w]) { cost[w] = c; next[w] = u; push(w, c); }
    }
  }
  const field: RouteField = { node, cost, next };
  memo.set(key, field);
  while (memo.size > ROUTE.fieldMemo) memo.delete(memo.keys().next().value!);
  return field;
}

/**
 * Where a trip from `from` to `dest` on `way` heads next — the string pulled: of the next ROUTE.lookHops vertices along its
 * field, the farthest whose straight line from `from` keeps every vertex before it within ROUTE.corridor of the mesh's
 * spacing (so open ground is walked straight, and the way bends only where the ground bends it); `dest` itself once the
 * goal's vertex is in that reach, or with no way.
 */
export function routeStep(planet: PlanetState, from: Vec3, dest: Vec3, way: RouteWay, roads?: Roads | null): Vec3 {
  const g = planetRouteGraph(planet), f = routeField(planet, dest, way, roads);
  const v = g.nodeOf[nearestSurfaceVertex(planet, from)];
  if (!Number.isFinite(f.cost[v])) return dest;
  const hops: number[] = [];
  for (let w = v, k = 0; k < ROUTE.lookHops && f.next[w] >= 0; k++) { w = f.next[w]; hops.push(w); }
  if (hops.length === 0) return dest;
  const spacing = Math.sqrt(4 * Math.PI / g.count), corridor = ROUTE.corridor * spacing;
  const a = from, al = Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
  let best = hops[0];
  for (let k = 1; k < hops.length; k++) {
    const b = dirOf(g, hops[k]);
    // the great circle from `from` to this hop: each vertex before it within the corridor of it
    let nx = a.y * b.z - a.z * b.y, ny = a.z * b.x - a.x * b.z, nz = a.x * b.y - a.y * b.x;
    const nl = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (nl < 1e-12 * al) { best = hops[k]; continue; }
    nx /= nl; ny /= nl; nz /= nl;
    let inside = true;
    for (let j = 0; j < k && inside; j++) {
      const p = dirOf(g, hops[j]);
      if (Math.abs(p.x * nx + p.y * ny + p.z * nz) > corridor) inside = false;
    }
    if (!inside) break;
    best = hops[k];
  }
  return best === f.node ? dest : dirOf(g, best);
}

/** A trip's cost from `from` to `dest` on `way` (flat-equivalent world units: ÷ a pace for seconds); Infinity with no way. */
export function routeCost(planet: PlanetState, from: Vec3, dest: Vec3, way: RouteWay, roads?: Roads | null): number {
  const g = planetRouteGraph(planet), f = routeField(planet, dest, way, roads);
  const v = g.nodeOf[nearestSurfaceVertex(planet, from)];
  return arcBetween(from, dirOf(g, v)) * planet.radius + f.cost[v] + arcBetween(dirOf(g, f.node), dest) * planet.radius;
}

/** The vertices of the trip from `from` to `dest` on `way`, in order (for the table and the tests); empty with no way. */
export function routePath(planet: PlanetState, from: Vec3, dest: Vec3, way: RouteWay, roads?: Roads | null): Vec3[] {
  const g = planetRouteGraph(planet), f = routeField(planet, dest, way, roads);
  let v = g.nodeOf[nearestSurfaceVertex(planet, from)];
  if (!Number.isFinite(f.cost[v])) return [];
  const out: Vec3[] = [dirOf(g, v)];
  while (f.next[v] >= 0) { v = f.next[v]; out.push(dirOf(g, v)); }
  return out;
}

/**
 * 74.5 step 2 (DECISIONS 357; core/roads.ts roadWorld): the world's rules for wheels on a planet with roads, per vertex —
 * `refuse`: never entered (a strong chain's vertices but its fords; a ford its side's enemy holds, for that side:
 * core/roads.ts roadsFor); `firm`: a playa's floor (crossed as a road); `bank`: a strong chain's bank, cheap while the roads
 * are laid; `lake`: a lake's floor, dear while they are laid — driven freely, as no water is drawn on it yet (74.3 step 2's
 * to refuse it) and bases stand on some. `bank` and `lake` are absent after the laying. `key` names it in the memos.
 */
export interface RoadWorld { key: string; refuse: Uint8Array; firm: Uint8Array; bank?: Uint8Array; lake?: Uint8Array }
/**
 * 216 (4): a planet's roads — the route edges they run on (keyed a·count + b, a < b), and a key for the memos. 74.5 step 2:
 * the trails' edges (from the settlements to the water and the ridge posts), the world's rules for wheels, and how many of
 * the spanning tree's links found no way under them (left out).
 */
export interface Roads { key: string; edges: Set<number>; trails?: Set<number>; world?: RoadWorld; unlinked?: number }
const roadMemo = new WeakMap<PlanetRouteGraph, Map<string, Roads>>();

/**
 * 216 (4) (the owner: "if we have like maybe like a town or like roads or like features there could be much more gameplay
 * around like the roads allow you to transit faster"; GameConfig.roads): the roads between `ends` (the settlements and the
 * bases) — one network, the ends joined by the shortest links that join them all (a spanning tree; each to its nearest
 * left pieces apart), each link the wheels route between its ends (without roads), the edges it uses the road. Pure,
 * memoised per planet and ends; null with fewer than two ends.
 */
export function planetRoads(planet: PlanetState, ends: readonly Vec3[], world?: RoadWorld | null, trailEnds?: ReadonlyArray<readonly [Vec3, Vec3]>): Roads | null {
  const g = planetRouteGraph(planet);
  const nodes = [...new Set(ends.map(d => g.nodeOf[nearestSurfaceVertex(planet, d)]))].sort((a, b) => a - b);
  if (nodes.length < 2) return null;
  const trailNodes = (trailEnds ?? []).map(([a, b]) => [g.nodeOf[nearestSurfaceVertex(planet, a)], g.nodeOf[nearestSurfaceVertex(planet, b)]] as const);
  const key = nodes.join(',') + (world ? `|${world.key}|${trailNodes.map(([a, b]) => `${a}>${b}`).join(',')}` : '');
  let memo = roadMemo.get(g);
  if (!memo) { memo = new Map(); roadMemo.set(g, memo); }
  const hit = memo.get(key);
  if (hit) return hit;
  // (74.5: laid under the world's rules — fords the only crossings, lakes refused, the bank cheap — when it is given)
  const lay: Roads | null = world ? { key: `lay|${world.key}`, edges: new Set(), world } : null;
  // one network: the ends joined by the shortest links that join them all (a spanning tree by the arc, Prim's from the first;
  // 74.5, with the world: of the links with a way under its rules — where the tree can grow no further, another starts from
  // the lowest end left, and an end no link joins is counted)
  const reaches = (a: number, b: number) => !lay || Number.isFinite(routeField(planet, dirOf(g, b), 'wheels', lay).cost[a]);
  const pairs = new Set<string>();
  const inTree = new Set<number>([nodes[0]]), cut = new Set<string>(), linked = new Set<number>();
  while (inTree.size < nodes.length) {
    let pa = -1, pb = -1, bestD = Infinity;
    for (const a of nodes) {
      if (!inTree.has(a)) continue;
      for (const b of nodes) { if (inTree.has(b) || cut.has(`${a}:${b}`)) continue; const d = arcBetween(dirOf(g, a), dirOf(g, b)); if (d < bestD) { pa = a; pb = b; bestD = d; } }
    }
    if (pa < 0) { inTree.add(nodes.find(v => !inTree.has(v))!); continue; }
    if (!reaches(pa, pb)) { cut.add(`${pa}:${pb}`); continue; }
    inTree.add(pb);
    linked.add(pa); linked.add(pb);
    pairs.add(pa < pb ? `${pa}:${pb}` : `${pb}:${pa}`);
  }
  const unlinked = nodes.length - linked.size;
  const edges = new Set<number>();
  for (const p of [...pairs].sort()) {
    const [a, b] = p.split(':').map(Number);
    const f = routeField(planet, dirOf(g, b), 'wheels', lay);
    if (!Number.isFinite(f.cost[a])) continue;
    for (let v = a; f.next[v] >= 0; v = f.next[v]) { const w = f.next[v]; edges.add(v < w ? v * g.count + w : w * g.count + v); }
  }
  if (!world) {
    const roads: Roads = { key: `r${key}`, edges };
    memo.set(key, roads);
    return roads;
  }
  // 74.5: the trails — each pair's walk on foot, its edges that are not road
  const trails = new Set<number>();
  for (const [a, b] of trailNodes) {
    if (a === b) continue;
    const f = routeField(planet, dirOf(g, b), 'foot');
    if (!Number.isFinite(f.cost[a])) continue;
    for (let v = a; f.next[v] >= 0; v = f.next[v]) { const w = f.next[v], k = v < w ? v * g.count + w : w * g.count + v; if (!edges.has(k)) trails.add(k); }
  }
  const { bank: _b, lake: _l, ...rules } = world;
  const roads: Roads = { key: `r${key}`, edges, trails, world: rules, unlinked };
  memo.set(key, roads);
  return roads;
}

/** §0.11 step B (2): one of the planet's main routes — the vertices of a path (directions, in order) on one planet, by one way. */
export interface MainRoute { planetIndex: number; way: RouteWay; points: Vec3[] }

/**
 * §0.11 step B (2) (lane F, for the dressing and the planet table): the match's **main routes** — base to base, and each base
 * to each objective (`modeState.points.sites`), on wheels, as the route graph has them (`routePath`), on the battle planet's
 * board (the board's bases; without a board, the sides' anchors) — **deduplicated where paths share vertices**: an edge
 * already on an earlier route is not repeated, so a later path contributes only its new runs (each run its own route; a
 * path wholly on earlier ones contributes nothing). Pure (the fields are memoised per destination), in the bases' and the
 * sites' order, so the list is the same for the same state. Empty with no two bases on one planet. With roads (216 (4),
 * GameConfig.roads) the caller passes each planet's (`roadsFor`: core/roads.ts `roadsOf`), and the routes follow them as a
 * drive would.
 */
export function mainRoutes(state: Pick<GameState, 'planets' | 'players'> & Partial<Pick<GameState, 'board' | 'modeState'>>, roadsFor?: (planetIndex: number) => Roads | null): MainRoute[] {
  const out: MainRoute[] = [];
  const planets = [...new Set(state.players.map(p => p.planetIndex))].sort((a, b) => a - b);
  for (const pi of planets) {
    const planet = state.planets[pi];
    if (!planet || planet.type !== 'planet') continue;
    let bases = (state.board?.bases ?? []).filter(b => b.planetIndex === pi).sort((x, y) => x.team - y.team).map(b => b.dir);
    if (bases.length < 2) {
      const sums = new Map<number, Vec3>();
      for (const p of state.players) { if (p.planetIndex !== pi) continue; const a = sums.get(p.teamIndex) ?? { x: 0, y: 0, z: 0 }; sums.set(p.teamIndex, { x: a.x + p.surfaceDir.x, y: a.y + p.surfaceDir.y, z: a.z + p.surfaceDir.z }); }
      bases = [...sums.entries()].sort((a, b) => a[0] - b[0]).map(([, v]) => { const l = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; });
    }
    if (bases.length < 2) continue;
    const sites = (state.modeState?.points?.sites ?? []).filter(s => s.planetIndex === pi).map(s => s.surfaceDir);
    const trips: Array<[Vec3, Vec3]> = [[bases[0], bases[bases.length - 1]]];
    for (const b of bases) for (const s of sites) trips.push([b, s]);
    const roads = roadsFor?.(pi) ?? null;
    const seen = new Set<string>();   // the edges laid so far, by their vertices' directions (the graph's own numbers: the same vertex, the same key)
    const key = (a: Vec3, b: Vec3) => { const ka = `${a.x},${a.y},${a.z}`, kb = `${b.x},${b.y},${b.z}`; return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`; };
    for (const [from, to] of trips) {
      const path = routePath(planet, from, to, 'wheels', roads);
      let run: Vec3[] = [];
      const flush = () => { if (run.length >= 2) out.push({ planetIndex: pi, way: 'wheels', points: run }); run = []; };
      for (let i = 1; i < path.length; i++) {
        const k = key(path[i - 1], path[i]);
        if (seen.has(k)) { flush(); continue; }
        seen.add(k);
        if (run.length === 0) run.push(path[i - 1]);
        run.push(path[i]);
      }
      flush();
    }
  }
  return out;
}
